import { uniq, zip } from 'lodash-es'
import { convertFractions } from '@/utilities/helpers'
import { abilityModifier } from '@/utilities/game-helpers'

const defaultSearch = {
  term: ``,
  type: ``,
  crMin: ``,
  crMax: ``,
}

const filters = {
  term: function({ name }) {
    return this.search.term === `` && this.getters.hasUserInput
      ? true
      : name.toLowerCase().indexOf(this.search.term.toLowerCase()) !== -1
  },
  type: function({ type }) {
    return (
      this.search.type === `` ||
      type.toLowerCase() === this.search.type.toLowerCase()
    )
  },
  crMin: function({ challenge_rating }) {
    const {
      getters: { monsterCRs },
      search,
    } = this
    const cr = {
      monster: monsterCRs.indexOf(challenge_rating),
      min: monsterCRs.indexOf(search.crMin),
    }
    return search.crMin === `` || cr.monster >= cr.min
  },
  crMax: function({ challenge_rating }) {
    const {
      getters: { monsterCRs },
      search,
    } = this
    const cr = {
      monster: monsterCRs.indexOf(challenge_rating),
      max: monsterCRs.indexOf(search.crMax),
    }

    return search.crMax === `` || cr.monster <= cr.max
  },
}

const bestiary = {
  namespaced: true,

  state: () => ({
    added: {},
    monsters: [],
    search: defaultSearch,
  }),

  getters: {
    baseURL() {
      return `https://api.open5e.com/monsters/?format=json`
    },

    url(state, { baseURL }) {
      return `${baseURL}&fields=name,slug,dexterity,type,challenge_rating&limit=9999`
    },

    loading(state) {
      return !state.monsters || state.monsters.length < 1
    },

    hasUserInput({ search }) {
      return Object.values(search).some(criteria => criteria !== ``)
    },

    filteredMonsters({ monsters, search }, getters) {
      return getters.hasUserInput
        ? Object.values(filters).reduce(
            (filtered, currentFilter) =>
              filtered.filter(currentFilter, {
                search,
                getters,
              }),
            [...monsters]
          )
        : []
    },

    monsterTypes(state, { loading }) {
      const types = [`any`]
      if (loading) return types

      const sortedUniqueTypes = uniq(
        state.monsters.map(({ type }) => type)
      ).sort()
      return [...types, ...sortedUniqueTypes]
    },

    monsterCRs(state, { loading }) {
      if (loading) return []
      const uniqueCRs = uniq(
        state.monsters.map(({ challenge_rating }) => challenge_rating)
      )
      const numCRs = uniqueCRs.map(convertFractions)
      return zip(uniqueCRs, numCRs)
        .sort((a, b) => a[1] - b[1])
        .map(cr => cr[0])
    },

    crMinIndex(state, { monsterCRs }) {
      return monsterCRs.indexOf(state.search.crMin)
    },

    crMaxIndex(state, { monsterCRs }) {
      return monsterCRs.indexOf(state.search.crMax)
    },
  },

  actions: {
    async setAllMonsters({ commit, dispatch }) {
      const storedMonsters = window.localStorage.getItem(`monsters`)
      let monsters
      if (storedMonsters) {
        monsters = JSON.parse(storedMonsters)
      } else {
        monsters = await dispatch(`allMonstersFromAPI`)
        window.localStorage.setItem(`monsters`, JSON.stringify(monsters))
      }
      commit(`doSetAllMonsters`, monsters)
    },

    async allMonstersFromAPI({ getters }) {
      const response = await fetch(getters.url)
      const json = await response.json()
      const transformedData = json.results.map(
        ({ slug, type, dexterity, ...rest }) => ({
          slug,
          id: slug, // `id` required for use in ItemAnimator
          initiative: abilityModifier(dexterity),
          dexterity,
          type: type.toLowerCase(),
          ...rest,
        })
      )
      return transformedData
    },

    async monsterFromAPI({ getters }, name) {
      const response = await fetch(`${getters.baseURL}&name=${name}`)
      const json = await response.json()
      const { dexterity, ...rest } = json.results[0]
      return {
        id: name, // `id` required for use in ItemAnimator
        initiative: abilityModifier(dexterity),
        dexterity,
        name,
        ...rest,
      }
    },

    addToBattle({ dispatch }, { amount, added, data }) {
      const toAdd = Array(amount).fill(data)
      toAdd.forEach((item, index) => {
        dispatch(
          `combatant/create`,
          { ...item, name: `${item.name} ${added + index + 1}` },
          { root: true }
        )
      })
    },
  },

  mutations: {
    doSetAllMonsters(state, payload) {
      state.monsters = payload
    },

    doUpdateSearch(state, payload) {
      state.search = Object.assign({}, state.search, payload)
    },

    doReset(state) {
      state.search = defaultSearch
    },
  },
}

export default bestiary
